import { Deduction, DeductionKind, DeductionListJson } from "@/api/deduction.tsx"
import { FieldType, OrgFieldConfig } from "@/api/org_field_config.tsx"
import { UserState } from "@/auth/user"
import { toast } from "@/components/ui/use-toast.ts"
import { format as formatDate, parseISO } from "date-fns"
import { copyToClipboard, exactCurrencyFormatter } from "@/utils"
import { GUAYAKI_ORG_ID } from "@/utils/guayaki-org"
import { month_list_field_value } from "@/shared/clearing_list.ts"
import { settlement_type } from "@/shared/settlement_type.ts"
import { format_custom_deduction_method_label } from "@/utils/format_custom_deduction_method"
import { signed_coding_amount } from "@/utils/transaction_amount"
import { app_base_url } from "@/utils/app_base_url"


// NETSUITE EXPORT NOTES FOR SUPER COFFEE:
// d.remote_accounting_id is the NS Internal ID
export const SUPER_COFFEE_ORG_SID = "01910f2d-42da-6dfe-8709-9c52567fbb12" // "01hz4y8g559dmxybw79h31_t2xr" // TEST - TUPER
export const GOODPOP_ORG_IDS = ["0199c686-4369-377e-752a-6d851beb4f53", "019a0334-7b52-2a71-2b71-e3f7ebbdbc55"]
export const COMPOSTIC_ORG_ID = "018fcb27-364d-4599-c246-6da1b141b21b"
export const TEST_ORG_ID = "01j0vbrzjm2nfg26hqnqhk_8hc7"
export const WHO_GIVES_A_CRAP_ORG_ID = "019766a9-9b6d-bba0-e133-ad74ab99a0b9"
// Saffron Road — migrating off PromoMash. The accounting export is renamed to match their PromoMash
// "Deduction invoices" report (see PROMOMASH_HEADER_MAP / PROMOMASH_CSV_COLUMNS below).
export const SAFFRON_ROAD_ORG_ID = "019e0578-2890-37aa-7c0c-2fd9fde8d2bb"
export { GUAYAKI_ORG_ID } from "@/utils/guayaki-org"

/** Round a dollar amount to cents (avoids float noise in CSV export). */
export function roundMoneyAmount(value: number | string | null | undefined): number {
  if (value === null || value === undefined || value === "") {
    return 0
  }
  const num = typeof value === "string" ? Number.parseFloat(value) : value
  if (Number.isNaN(num)) {
    return 0
  }
  return Math.round(num * 100) / 100
}

/** Guayaki NetSuite columns populated from RTM auto-split code descriptions and split lines. */
export const GUAYAKI_RTM_SPLIT_EXPORT_COLUMNS = [
  "items_line_number",
  "execution_date",
  "items_deptId",
  "items_classId",
  "items_regionId",
  "items_channelId",
  "items_priceLevel",
  "items_item",
  "items_itemId",
  "items_description",
  "items_quantity",
  "items_proposed_rate",
  "items_rate",
  "cm_deptId",
  "cm_classId",
  "cm_regionId",
  "cm_channelId",
] as const

/** Meaningful columns from Yerba Madre's reviewed KeHE NetSuite credit-memo workbook, in import order. */
export const GUAYAKI_NETSUITE_CSV_COLUMNS = [
  "items_line_number",
  "kind",
  "invoice_amount",
  "discount_amount",
  "gross_amount",
  "invoice_date",
  "invoice_number",
  "check_number",
  "po_number",
  "dc",
  "retailer_name",
  "execution_date",
  "description",
  "category",
  "category_description",
  "status_value",
  "customer",
  "customer_id",
  "date",
  "external_id",
  "location",
  "memo",
  "cm_deptId",
  "cm_classId",
  "cm_regionId",
  "cm_channelId",
  "items_deptId",
  "items_classId",
  "items_regionId",
  "items_channelId",
  "items_priceLevel",
  "expense_account",
  "split_amount",
  "reason_code_name",
  "account_number",
  "items_item",
  "items_itemId",
  "items_description",
  "items_quantity",
  "items_proposed_rate",
  "items_rate",
] as const

/** Merge RTM-split columns into a base export selection for Guayaki. */
export function mergeGuayakiRtmSplitExportColumns(
  baseColumns: string[],
  availableColumns: string[]
): string[] {
  const merged = [...baseColumns]
  for (const column of GUAYAKI_RTM_SPLIT_EXPORT_COLUMNS) {
    if (availableColumns.includes(column) && !merged.includes(column)) {
      merged.push(column)
    }
  }
  return merged
}

type AccountingHeaderMap = Record<string, Record<string, string>>

// PromoMash "Deduction invoices" report headers. Brands migrating off PromoMash export the accounting
// table with these column names so their team can keep reconciling against the old report. Shared so
// registering the next migrating brand is a one-line entry in ACCOUNTING_HEADER_MAP.
// Note: `clearing_list`, `month_end_close_list` and `is_trade` are org fields — their headers come from OrgFieldConfig
// display_name (via orgFieldHeaderMap in buildCsv), so they are intentionally omitted here.
export const PROMOMASH_HEADER_MAP: Record<string, string> = {
  check_number: "Check number",
  invoice_number: "Deduction invoice",
  kind: "Settlement type",
  source: "Direct PM customer",
  invoice_amount: "Amount",
  admin_fee: "Fees",
  discount_amount: "Adjustment",
  split_amount: "Net amount",
  reason_code_name: "Fund",
  account_number: "GL account",
  status_value: "Validation status",
  cleared: "Cleared",
  validated_date: "Closed date",
}

// Internal field names in PromoMash "Deduction invoices" column order. Drives the default selected
// columns (and therefore export order) for migrating brands. `clearing_list` / `month_end_close_list` / `is_trade` are org
// fields and only appear if the org has those OrgFieldConfig entries active.
export const PROMOMASH_CSV_COLUMNS = [
  "clearing_list",
  "check_number",
  "invoice_number",
  "kind",
  "source",
  "invoice_amount",
  "admin_fee",
  "discount_amount",
  "split_amount",
  "reason_code_name",
  "account_number",
  "status_value",
  "cleared",
  "validated_date",
  "month_end_close_list",
  "is_trade",
]

const ACCOUNTING_HEADER_MAP: AccountingHeaderMap = {
  // Guayaki
  [GUAYAKI_ORG_ID]: {
    // Map internal field names to exact CSV column names
    po_number: "PO #",
    execution_date: "Items: MCB Date",
    items_deptId: "Items: DeptID",
    items_classId: "Items: classID",
    items_regionId: "Items: regionID",
    items_channelId: "Items: channelID",
    items_priceLevel: "Items: Price Level",
    items_item: "Items: Item",
    items_itemId: "Items: ItemID",
    items_description: "Items: Description",
    items_quantity: "Items: Quantity",
    items_proposed_rate: "Items: Proposed Rate",
    items_rate: "Items: Rate",
    items_line_number: "Items: Line Number",
    customer_id: "Customer ID",
    customer: "Customer",
    date: "Date",
    location: "Location",
    external_id: "External ID",
    // Add the new CM field mappings
    cm_deptId: "CM: deptID",
    cm_classId: "CM: classID",
    cm_regionId: "CM: regionID",
    cm_channelId: "CM: channelID",
    memo: "Memo",
  },
  // Super Coffee
  [SUPER_COFFEE_ORG_SID]: {
    expense_amount: "Expense_Amount",
    deduction_id: "Deduction_ID",
    expense_memo: "Expense_Memo",
    reason_code: "Reason_Code",
    account_number: "Account_Number",
  },
  // Compostic / Xero
  [COMPOSTIC_ORG_ID]: {
    contact_name: "*ContactName",
    invoice_number: "*InvoiceNumber",
    invoice_date: "*InvoiceDate",
    due_date: "*DueDate",
    quantity: "*Quantity",
    account_code: "*AccountCode",
    tax_type: "*TaxType",
    split_amount: "*UnitAmount",
  },
  // Test org for unit tests
  [TEST_ORG_ID]: {
    invoice_amount: "UnitPrice",
    expense_account: "ExpenseAccount",
  },
  // Saffron Road (migrating off PromoMash). Add future migrating brands with the same shared map.
  [SAFFRON_ROAD_ORG_ID]: PROMOMASH_HEADER_MAP,
  // Who Gives A Crap
  [WHO_GIVES_A_CRAP_ORG_ID]: {
    invoice_number: "Invoice #",
    invoice_date: "Invoice/Claims Date",
    date_approved: "Date Approved",
    approver_name: "Approver's Name",
    memo: "Memo or Description",
    product_line: "Item",
    net_amount: "Net Amount",
    gross_amount: "Gross Amount",
    reason_code_name: "Claims Category",
  },
}

const CSV_EXPORT_TABLE_CONTEXTS = ["accounting_table", "check_detail_table"]

function isCsvExportOrgField(config: OrgFieldConfig): boolean {
  return config.is_active && CSV_EXPORT_TABLE_CONTEXTS.some((context: string) => config.table_contexts.includes(context))
}

export function buildCsv(
  filteredData: Record<string, any>[],
  org_id: string,
  orgFieldConfigs?: OrgFieldConfig[]
): string {
  const HEADER_MAP = ACCOUNTING_HEADER_MAP[org_id] || {}

  // Create a map of field_name to display_name for org fields
  const orgFieldHeaderMap: Record<string, string> = {}
  if (orgFieldConfigs) {
    for (const config of orgFieldConfigs) {
      if (isCsvExportOrgField(config)) {
        orgFieldHeaderMap[config.field_name] = config.display_name
      }
    }
  }

  // Get all possible headers while preserving order
  const headerSet = new Set<string>()

  // First add headers from the first item to maintain primary column order
  if (filteredData[0]) {
    Object.keys(filteredData[0]).forEach(key => headerSet.add(key))
  }

  // Then add any additional headers from other items
  filteredData.forEach(item => {
    Object.keys(item).forEach(key => headerSet.add(key))
  })

  // Convert to array while preserving order
  const baseHeaders = Object.keys(filteredData[0] || {})
  const additionalHeaders = Array.from(headerSet).filter(h => !baseHeaders.includes(h))
  const headers = [...baseHeaders, ...additionalHeaders]

  // Create header row - use org field display_name if available, otherwise use HEADER_MAP or field name
  const headerRow = headers
    .map(header => {
      const displayName = orgFieldHeaderMap[header] || HEADER_MAP[header] || header
      return `"${displayName}"`
    })
    .join(",")

  // Create body rows using ordered headers
  const body = filteredData
    .map(item => {
      return headers
        .map(header => {
          const cellValue = item[header]
          return `"${cellValue != null ? cellValue : ""}"`
        })
        .join(",")
    })
    .join("\n")

  return [headerRow, body].join("\n")
}

export function getAvailableExportColumns(filteredData: Record<string, any>[]): string[] {
  if (!filteredData.length) {
    return []
  }

  const headerSet = new Set<string>()

  const baseHeaders = Object.keys(filteredData[0] || {})
  baseHeaders.forEach(header => headerSet.add(header))

  filteredData.forEach(item => {
    Object.keys(item).forEach(key => headerSet.add(key))
  })

  const additionalHeaders = Array.from(headerSet).filter(header => !baseHeaders.includes(header))
  return [...baseHeaders, ...additionalHeaders]
}

export function filterDownloadDataBySelectedColumns(
  filteredData: Record<string, any>[],
  selectedColumns: string[]
): Record<string, any>[] {
  if (!filteredData.length) {
    return []
  }

  const availableColumns = getAvailableExportColumns(filteredData)
  const validSelectedColumns = selectedColumns.filter((column: string) => availableColumns.includes(column))

  if (!validSelectedColumns.length) {
    return filteredData
  }

  return filteredData.map((item: Record<string, any>) => {
    return Object.fromEntries(validSelectedColumns.map((column: string) => [column, item[column]]))
  })
}

export function downloadCsv(csvData: string, filename: string) {
  const blob = new Blob([csvData], { type: "text/csv" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url

  // Format the download filename with the current date
  const today = new Date()
  let year = today.getFullYear()
  let month = String(today.getMonth() + 1).padStart(2, "0")
  let day = String(today.getDate()).padStart(2, "0")
  const formattedDate = `${year}-${month}-${day}`
  a.download = `${filename}_${formattedDate}.csv`
  // Trigger the download and clean up the URL object
  a.click()
  URL.revokeObjectURL(url)
}

function formatDateForIif(dateStr?: string | null): string {
  if (!dateStr) return ""
  try {
    const d = parseISO(String(dateStr))
    if (isNaN(d.getTime())) return ""
    // QuickBooks IIF format: MM/DD/YYYY
    return formatDate(d, "MM/dd/yyyy")
  } catch (_) {
    return ""
  }
}

function escapeIifValue(value: any): string {
  if (value == null || value === "") return ""
  // IIF files use tab-delimited format, so we need to escape tabs and newlines
  return String(value).replace(/\t/g, " ").replace(/\n/g, " ").replace(/\r/g, "")
}

export function buildIif(filteredData: Record<string, any>[]): string {
  if (!filteredData.length) return ""

  // IIF format for journal entries
  // Header rows with column names (at the top of the file)
  const headerRow = "!TRNS\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO\tREFNUM\tCHECKNUM"
  const splitHeaderRow = "!SPL\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO"
  const endHeaderRow = "!ENDTRNS"

  const lines: string[] = [headerRow, splitHeaderRow, endHeaderRow]

  // Group data by transaction (deduction) to create proper TRNS/SPL pairs
  // Each deduction becomes one TRNS with multiple SPL rows
  const transactionGroups = new Map<string, Record<string, any>[]>()

  for (const item of filteredData) {
    // Use deduction_id or id as the transaction key
    const transactionKey = item.deduction_id || item.id || String(Math.random())
    if (!transactionGroups.has(transactionKey)) {
      transactionGroups.set(transactionKey, [])
    }
    transactionGroups.get(transactionKey)!.push(item)
  }

  // Generate IIF entries for each transaction group
  for (const [transactionKey, items] of transactionGroups) {
    if (items.length === 0) continue

    const firstItem = items[0]

    // Get date - prefer invoice_date, then execution_date
    const date = formatDateForIif(firstItem.invoice_date || firstItem.execution_date)

    // Get customer/retailer name
    const name = escapeIifValue(firstItem.customer_name || firstItem.retailer_name || firstItem.customer || "")

    // Get invoice number and check number
    const refnum = escapeIifValue(firstItem.invoice_number || "")
    const checknum = escapeIifValue(firstItem.check_number || "")

    // Calculate total amount (sum of all splits, negative for deductions)
    let totalAmount = 0
    for (const item of items) {
      const amount = item.split_amount || item.reason_code_amount || item.invoice_amount || 0
      totalAmount += Math.abs(amount)
    }
    // Make negative for deductions
    totalAmount = -Math.abs(totalAmount)

    // Get memo from first item
    const memo = escapeIifValue(
      firstItem.memo || firstItem.code_description || firstItem.description || firstItem.reason_code_code_description || ""
    )

    // TRNS row (main transaction)
    const trnsRow = [
      "TRNS",
      "GENERAL JOURNAL",
      date,
      "", // ACCNT - left empty for main transaction
      name,
      totalAmount.toFixed(2),
      memo,
      refnum,
      checknum,
    ].join("\t")

    lines.push(trnsRow)

    // SPL rows (one per split/item)
    for (const item of items) {
      const splitAmount = item.split_amount || item.reason_code_amount || item.invoice_amount || 0
      const splitAccount = escapeIifValue(
        item.expense_account || item.account_number || item.reason_code_account_number || ""
      )
      const splitMemo = escapeIifValue(
        item.memo || item.code_description || item.reason_code_code_description || item.description || ""
      )

      // SPL row
      const splRow = [
        "SPL",
        "GENERAL JOURNAL",
        date,
        splitAccount,
        name,
        Math.abs(splitAmount).toFixed(2), // Positive amount for SPL
        splitMemo,
      ].join("\t")

      lines.push(splRow)
    }

    // End transaction marker
    lines.push("ENDTRNS")
  }

  return lines.join("\n")
}

export function downloadIif(iifData: string, filename: string) {
  const blob = new Blob([iifData], { type: "text/plain" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url

  // Format the download filename with the current date
  const today = new Date()
  let year = today.getFullYear()
  let month = String(today.getMonth() + 1).padStart(2, "0")
  let day = String(today.getDate()).padStart(2, "0")
  const formattedDate = `${year}-${month}-${day}`
  a.download = `${filename}_${formattedDate}.iif`
  // Trigger the download and clean up the URL object
  a.click()
  URL.revokeObjectURL(url)
}

// Add portal link based on current page context
function addPortalLink(item: any): string {
  const base = app_base_url()
  const isAccountingPage =
    typeof window !== "undefined" && window.location.pathname.includes("accounting")
  const id = item.deduction_id ?? item.id

  if (isAccountingPage) {
    return `${base}/accounting/transaction/${id}`
  } else {
    return `${base}/deduction/${id}`
  }
}

export function isGoodPop(): boolean {
  const user = UserState.get()
  return Boolean(user?.org_id && GOODPOP_ORG_IDS.includes(user.org_id as string))
}

function formatYearMonth(dateStr?: string | null): string | undefined {
  if (!dateStr) return undefined
  // If already looks like YYYY-MM, keep it
  if (/^\d{4}-\d{2}$/.test(dateStr)) return dateStr
  try {
    const d = parseISO(String(dateStr))
    if (isNaN(d.getTime())) return undefined
    return formatDate(d, "yyyy-MM")
  } catch (_) {
    return undefined
  }
}

function applyGoodPopDateFormatting(obj: Record<string, any>): void {
  if (!isGoodPop()) return
  if (obj.execution_date) {
    const ym = formatYearMonth(obj.execution_date)
    if (ym) obj.execution_date = ym
  }
  if (obj.reason_code_execution_date) {
    const ym = formatYearMonth(obj.reason_code_execution_date)
    if (ym) obj.reason_code_execution_date = ym
  }
}

// Add this type to help with code description parsing
type ParsedCodeDescription = {
  deptId?: string
  classId?: string
  regionId?: string
  channelId?: string
  priceLevel?: string
}

// Add helper function to parse code description
function parseCodeDescription(description?: string): ParsedCodeDescription {
  if (!description) return {}

  const result: ParsedCodeDescription = {}

  // Match patterns like "Items: DeptID-462" and extract the value after the dash
  const patterns = {
    deptId: /Items: DeptID-(\d+)/,
    classId: /Items: classID-(\d+)/,
    regionId: /Items: regionID-(\d+)/,
    channelId: /Items: channelID-(\d+)/,
    priceLevel: /Items: Price Level-(\w+)/,
  }

  Object.entries(patterns).forEach(([key, pattern]) => {
    const match = description.match(pattern)
    if (match?.[1]) {
      result[key as keyof ParsedCodeDescription] = match[1]
    }
  })

  return result
}

// Add this helper function
function getExcelSerialDate(date: Date): number {
  // Excel's epoch is 1/1/1900 and Excel incorrectly considers 1900 as leap year
  const EXCEL_EPOCH = new Date(1899, 11, 31) // Dec 31, 1899
  const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

  // Get days between dates
  const utcDate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  const utcExcelEpoch = Date.UTC(EXCEL_EPOCH.getFullYear(), EXCEL_EPOCH.getMonth(), EXCEL_EPOCH.getDate())

  // Add 1 to account for Excel's leap year bug in 1900
  return Math.floor((utcDate - utcExcelEpoch) / MILLISECONDS_PER_DAY) + 1
}

// Add helper function to handle Guayaki-specific transformations
function addGuayakiSpecificFields(baseItem: Record<string, any>, item: any, arrayItem?: any) {
  const user = UserState.get()
  if (!user?.org_id || user.org_id !== GUAYAKI_ORG_ID) return

  // Get today's date as Excel serial number
  const excelDate = getExcelSerialDate(new Date())

  // Parse code description for the required fields
  const description = arrayItem?.code_description ?? (item as any).code_description
  const parsedDesc = parseCodeDescription(description)

  // Add only the required fields
  Object.assign(baseItem, {
    // Standard fields
    customer: "KeHE",
    customer_id: "107475",
    date: new Date().toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }),
    external_id: `107475-${excelDate}${item.check_number ? item.check_number : String.fromCharCode(97 + Math.floor(Math.random() * 26))}`,
    location: item.location || "GSRP HQ",
    // po_number: item.po_number,
    memo: item.memo || "MarginWiz Deduction Platform - Accounting Export",

    cm_deptId: "462",
    cm_classId: "362",
    cm_regionId: "17",
    cm_channelId: "2",

    // Items fields from parsed description
    items_deptId: parsedDesc.deptId || "",
    items_classId: parsedDesc.classId || "",
    items_regionId: parsedDesc.regionId || "",
    items_channelId: parsedDesc.channelId || "",
    // Every row in Guayaki's NetSuite credit-memo import uses Custom pricing. Do not make
    // a missing RTM metadata token turn this into a blank import column.
    items_priceLevel: parsedDesc.priceLevel || "Custom",
  })

  // Add fields that depend on arrayItem
  if (arrayItem) {
    const executionDateValue = arrayItem.execution_date || item.execution_date || item.invoice_date
    const executionDate = executionDateValue ? new Date(executionDateValue) : null
    // Updated format to "MMM YYYY" (e.g. "Dec 2023")
    const mcbDate = executionDate && !Number.isNaN(executionDate.getTime())
      ? `${executionDate.toLocaleString("en-US", { month: "short" })} ${executionDate.getFullYear()}`
      : ""

    const itemName = arrayItem.expense_account || arrayItem.name || baseItem.reason_code_expense_account || ""
    const itemInternalId = arrayItem.account_remote_id || arrayItem.remote_id || ""
    const categoryAndDc = [item.category_description, item.dc_name || item.dc].filter(Boolean).join(" - ")
    const descriptionPrefix = item.category_description ? categoryAndDc : itemName
    const itemDescription = [descriptionPrefix, item.retailer_name, mcbDate].filter(Boolean).join(": ")

    Object.assign(baseItem, {
      items_item: itemName,
      items_itemId: itemInternalId,
      items_description: itemDescription,
      items_quantity: "1",
      items_proposed_rate: Math.abs(arrayItem.amount).toFixed(2),
      items_rate: "0.00",
      execution_date: mcbDate,
      reason_code_amount: Math.abs(arrayItem.amount).toFixed(2), // todo: maybe handle decimal splitting better on the backend?
    })
  }
}

// Add helper function to format Super Coffee data for NetSuite export
function formatSuperCoffeeData(item: any, reasonCode: any, split?: any, baseItem?: any): Record<string, any> {
  // account_number is already set in baseItem for all brands and will be mapped to Account_Number via header map
  return {
    Expense_Amount: Math.abs(reasonCode.amount || split?.amount || 0).toFixed(2),
    Deduction_ID: `- iTPM Deduction #${item.accounting_number || ""}`,
    Expense_Memo: reasonCode.code_description || split?.code_description || "",
    Reason_Code: reasonCode.name || split?.expense_account || split?.name || "",
  }
}

// Add helper function to handle Super Coffee-specific transformations
function addSuperCoffeeSpecificFields(baseItem: Record<string, any>, item: any, reasonCode: any, split?: any) {
  const user = UserState.get()
  if (!user?.org_id || user.org_id !== SUPER_COFFEE_ORG_SID) return

  // Only process if we have a valid reason code or split
  if (!reasonCode && !split) return

  // Format the data according to NetSuite requirements
  const formattedData = formatSuperCoffeeData(item, reasonCode || {}, split, baseItem)
  Object.assign(baseItem, formattedData)
}

// Helper function to extract account number from expense_account field
// Extracts leading numeric digits (e.g., "5041 : Sales Promotions" → "5041")
function extractAccountNumber(expenseAccount: string | undefined): string {
  if (!expenseAccount) return ""
  const match = expenseAccount.match(/^\d+/)
  return match ? match[0] : ""
}

// Helper function to remap Kehe contact names for Compostic
function remapKeheContactName(contactName: string): string {
  if (!contactName) return contactName

  // Check if contact_name contains semicolons
  if (contactName.includes(";")) {
    // Split by semicolon, convert each part to lowercase, and check if any part is "kehe"
    const parts = contactName.split(";").map((part: string) => part.trim().toLowerCase())
    if (parts.some((part: string) => part === "kehe")) {
      return "Kehe Distributors LLC"
    }
  }

  // Check if the contact_name itself (when lowercased) is "kehe"
  if (contactName.trim().toLowerCase() === "kehe") {
    return "Kehe Distributors LLC"
  }

  return contactName
}

// Add helper function to handle Xero-specific transformations
function addXeroSpecificFields(baseItem: Record<string, any>, item: any, split?: any) {
  const user = UserState.get()
  if (!user?.org_id || user.org_id !== COMPOSTIC_ORG_ID) return

  // Get expense_account from baseItem (which may have been set from split.name or split.expense_account),
  // or from split directly, or from item
  const expenseAccount = baseItem.expense_account || split?.expense_account || split?.name || item.expense_account || ""
  const accountCode = extractAccountNumber(expenseAccount)

  // Get due date - use payment_due_date if available, otherwise invoice_date
  const dueDate = item.payment_due_date || item.invoice_date || baseItem.invoice_date || ""

  // Set required Xero fields (only include fields with values)
  const contactName = item.customer_name || baseItem.customer_name || ""
  const remappedContactName = remapKeheContactName(contactName)
  const invoiceNumber = item.invoice_number || baseItem.invoice_number || ""
  const invoiceDate = item.invoice_date || baseItem.invoice_date || ""

  Object.assign(baseItem, {
    contact_name: remappedContactName,
    invoice_number: invoiceNumber,
    invoice_date: invoiceDate,
    due_date: dueDate,
    quantity: "1",
    account_code: accountCode,
    tax_type: "",
  })
}

// Add helper function to handle Who Gives A Crap-specific transformations
function addWhoGivesACrapSpecificFields(baseItem: Record<string, any>, item: any, split?: any, reasonCode?: any) {
  const user = UserState.get()
  if (!user?.org_id || user.org_id !== WHO_GIVES_A_CRAP_ORG_ID) return

  const invoiceDate = formatDate(item.invoice_date || baseItem.invoice_date, "MM/dd/yyyy")

  // Get the date when the item was coded - use the earliest split's created_at, or fall back to item's updated_at
  let dateApproved = ""
  if (split?.created_at) {
    dateApproved = formatDate(parseISO(split.created_at), "MM/dd/yyyy")
  } else if (item.splits && Array.isArray(item.splits) && item.splits.length > 0) {
    // Find the earliest split's created_at
    const splitDates = item.splits
      .map((s: any) => s.created_at)
      .filter((d: any) => d != null)
      .map((d: string) => new Date(d).getTime())
    if (splitDates.length > 0) {
      const earliestDate = new Date(Math.min(...splitDates))
      dateApproved = formatDate(earliestDate, "MM/dd/yyyy")
    }
  }
  // Fall back to item's updated_at if no split dates found
  if (!dateApproved && (item.updated_at || baseItem.updated_at)) {
    dateApproved = formatDate(parseISO(item.updated_at || baseItem.updated_at), "MM/dd/yyyy")
  }

  const invoiceNumber = item.invoice_number || baseItem.invoice_number || ""

  // Get memo/description - prefer memo, then code_description, then description
  const memo =
    baseItem.memo ||
    reasonCode?.code_description ||
    split?.code_description ||
    item.description ||
    baseItem.description ||
    ""

  // Get product line (SKU)
  const productLine = item.product_line || baseItem.product_line || ""

  // Get net amount - prefer split_amount (already in baseItem), then reason_code_amount, then invoice_amount
  const netAmount =
    baseItem.split_amount !== undefined
      ? baseItem.split_amount
      : baseItem.reason_code_amount !== undefined
        ? baseItem.reason_code_amount
        : split?.amount !== undefined
          ? split.amount
          : reasonCode?.amount !== undefined
            ? reasonCode.amount
            : item.invoice_amount !== undefined
              ? item.invoice_amount
              : baseItem.invoice_amount || 0
  // Format as currency and extract just the number (remove $ and commas for CSV)
  const netAmountFormatted = exactCurrencyFormatter(Math.abs(netAmount)).replace(/[$,]/g, "")

  // Get gross amount
  const grossAmount = item.gross_amount !== undefined ? item.gross_amount : baseItem.gross_amount || 0
  // Format as currency and extract just the number (remove $ and commas for CSV)
  const grossAmountFormatted = exactCurrencyFormatter(Math.abs(grossAmount)).replace(/[$,]/g, "")

  Object.assign(baseItem, {
    invoice_number: invoiceNumber,
    invoice_date: invoiceDate,
    date_approved: dateApproved,
    approver_name: "Dalton Simmons",
    memo: memo,
    product_line: productLine,
    net_amount: netAmountFormatted,
    gross_amount: grossAmountFormatted,
  })
}

const INTERNAL_KEYS = new Set([
  "created_at",
  "updated_at",
  "raw_data",
  "s3_uri",
  "backup_s3_uri",
  "check_s3_uri",
  "repayment_for",
  "notes",
  "document",
  "user_files",
  "amount_remaining",
  // "custom_deduction_method", // exported as its display label so direct bills (check requests) can be filtered
  // "accounting_number", // adding back in for super coffee export
  // "original_source", // adding back in for super coffee export
  "parent_accounting_number",
  "accounting_status",
  "has_splits",
  "is_parent",
  "dc_name",
  "original_source",
  "remote_accounting_id",
  "task",
  "pending",
  "validated",
  "can_auto_code",
  "coded",
  "splits",
])

/** Matches the server's label for a split with no reason code (`accounting/handler.rs`). */
const UNCODED_LABEL = "Uncoded"

/**
 * Columns that exist on both a transaction and its GL splits. An export row *is* one split, so
 * where the split carries its own value it describes that row better than the transaction-level
 * column does; the transaction value stays as the fallback. `execution_date` always behaved this
 * way — the rest did not, so a coded Nancy's check exported the transaction's (usually empty)
 * product line and retailer on every split line instead of the ones the coder actually picked.
 */
const SPLIT_OVERRIDE_FIELDS = ["execution_date", "product_line", "retailer_name", "retailer_invoice_number"]

/**
 * "Net amount" for a transaction carrying no splits at all: what still has to be booked to a GL
 * account. Returns `""` — leaving the cell blank, as before — when there is nothing to book, and for
 * prepayments, whose `:P`/`:N` rows are a cash-application artifact that nets to zero and is never
 * coded; giving those an amount would inflate the file by both halves.
 */
export function uncodedExportAmount(row: {
  kind?: DeductionKind
  invoice_amount?: number | null
  discount_amount?: number | null
}): number | "" {
  if (!row.kind || row.kind === "prepayment") {
    return ""
  }
  const amount = signed_coding_amount(row.kind, row.invoice_amount ?? 0, row.discount_amount)
  return amount === 0 ? "" : roundMoneyAmount(amount)
}

/**
 * What is still unbooked on a transaction that carries *some* splits — the coding target less what
 * the splits already cover, in cents so a half-coded row doesn't drift.
 *
 * `uncodedExportAmount` only fires when a transaction has no splits at all, so a partially coded
 * one silently lost the difference: two UNFI West deductions on Saffron Road's check 1725134 are
 * $1,104.99 each with $144.13 coded, and the month-end file reported $288.26 where it owed
 * $2,209.98. Returns `""` when the splits close the transaction (the normal case) and for
 * prepayments, which are never coded.
 */
export function uncodedRemainderAmount(
  row: {
    kind?: DeductionKind
    invoice_amount?: number | null
    discount_amount?: number | null
  },
  splits: { amount?: number | string | null }[]
): number | "" {
  if (!row.kind || row.kind === "prepayment") {
    return ""
  }
  const target_cents = Math.round(signed_coding_amount(row.kind, row.invoice_amount ?? 0, row.discount_amount) * 100)
  const coded_cents = splits.reduce((sum: number, split: { amount?: number | string | null }) => {
    const amount = Number(split?.amount ?? 0)
    return sum + (Number.isFinite(amount) ? Math.round(amount * 100) : 0)
  }, 0)
  const remainder_cents = target_cents - coded_cents
  return remainder_cents === 0 ? "" : roundMoneyAmount(remainder_cents / 100)
}

export function collectDownloadData(data: any[], orgFieldConfigs?: OrgFieldConfig[]) {
  let filteredData = []
  if (!data.length) {
    return []
  }
  // Union of keys across all rows (first-row order preserved, stragglers appended). The server
  // omits Option fields (e.g. validated_date via skip_serializing_if) from the JSON, so keying
  // off data[0] alone would drop a column whenever the first row happens to lack it.
  const fieldSet = new Set<string>()
  for (const row of data) {
    for (const key of Object.keys(row)) {
      fieldSet.add(key)
    }
  }
  let fields = Array.from(fieldSet)
    .filter(h => !INTERNAL_KEYS.has(h) || h === "remote_accounting_id") // Keep remote_accounting_id for export
    .filter(h => (h !== "id" && !h.endsWith("_id")) || h === "remote_accounting_id")

  // Get active org field configs for CSV-exportable contexts
  const activeOrgFields = orgFieldConfigs?.filter((config: OrgFieldConfig) => isCsvExportOrgField(config)) || []

  // Helper function to get org field value (same logic as createAccountingOrgColumns)
  function getOrgFieldValue(row: any, fieldName: string, split?: any): string {
    const month_list = month_list_field_value(fieldName, row)
    if (month_list !== null) {
      return month_list
    }
    if (fieldName === "promotion_remote_ids") {
      return split?.promotion_remote_id || row.promotion_remote_ids || ""
    }
    // Transaction-level fields live on the row, not the split. `remote_accounting_id` is exported
    // unconditionally too, so falling through to the split would overwrite it with "" for any org
    // that also configures it as a field.
    if (
      fieldName === "posting_date" ||
      fieldName === "validated_date" ||
      fieldName === "remote_accounting_id" ||
      fieldName === "pushed_at"
    ) {
      return row[fieldName] || ""
    }
    if (fieldName === "is_trade") {
      const source = split ?? row.splits?.[0]
      const v = source?.is_trade
      return v === true ? "true" : v === false ? "false" : ""
    }
    // Split-level fields (incl. promotion_start_date / promotion_end_date): prefer the explicit
    // split for per-split export rows, else fall back to the first split.
    const source = split ?? row.splits?.[0]
    return (source?.[fieldName as keyof typeof source] as string) || ""
  }

  for (const [rowIndex, row] of data.entries()) {
    let filtered = Object.fromEntries(fields.map(field => [field, row[field]]))
    if (UserState.get()?.org_id === GUAYAKI_ORG_ID) {
      // NetSuite identifies the transaction line, so every allocation row produced from the same
      // transaction must keep the same number (matching the reviewed workbook's split rows).
      filtered.items_line_number = rowIndex + 1
    }

    // Add org field values
    for (const orgField of activeOrgFields) {
      filtered[orgField.field_name] = getOrgFieldValue(row, orgField.field_name)
    }

    filtered.portal_link = addPortalLink(row)
    // PromoMash "Cleared" column: yes once the deduction is validated. Available for all orgs but only
    // selected by default for migrating brands (PROMOMASH_CSV_COLUMNS).
    filtered.cleared = row.validated ? "yes" : "no"
    // "Settlement type": a direct billing is stored as kind = deduction but must read apart from one.
    if ("kind" in filtered) {
      filtered.kind = settlement_type(row)
    }
    if ("custom_deduction_method" in filtered) {
      const method = format_custom_deduction_method_label(row.custom_deduction_method)
      filtered.custom_deduction_method = method === "—" ? "" : method
    }
    addGuayakiSpecificFields(filtered, row)
    addXeroSpecificFields(filtered, row)
    addWhoGivesACrapSpecificFields(filtered, row)
    applyGoodPopDateFormatting(filtered)

    // Handle reason_codes array
    if (Array.isArray(row.reason_codes) && row.reason_codes.length > 0) {
      for (let reasonCode of row.reason_codes) {
        let filtered2 = Object.assign({}, filtered, {
          reason_code_code_description: reasonCode.code_description,
          reason_code_amount: roundMoneyAmount(reasonCode.amount),
          reason_code_execution_date: reasonCode.execution_date,
          reason_code_account_number:
            reasonCode.account_number ||
            (reasonCode.expense_account ? extractAccountNumber(reasonCode.expense_account) : ""),
        })
        // Re-add org field values for this split row
        for (const orgField of activeOrgFields) {
          filtered2[orgField.field_name] = getOrgFieldValue(row, orgField.field_name)
        }
        filtered2.portal_link = addPortalLink(row)
        addSuperCoffeeSpecificFields(filtered2, row, null, reasonCode)
        addGuayakiSpecificFields(filtered2, row, reasonCode)
        addWhoGivesACrapSpecificFields(filtered2, row, undefined, reasonCode)
        applyGoodPopDateFormatting(filtered2)
        filteredData.push(filtered2)
      }
    } else if (Array.isArray(row.splits) && row.splits.length > 0) {
      for (let split of row.splits) {
        let filtered2 = Object.assign({}, filtered, {
          expense_account: split.name || split.expense_account,
          split_amount: roundMoneyAmount(split.amount),
          reason_code_name: split.name || split.expense_account,
          account_number: split.account_number || "",
          // The coding detail captured on the split itself. Only the `reason_codes` branch used to
          // emit these, so an accounting/check export of coded rows dropped every field the coder
          // wrote (memo, promo type, product category) and repeated the transaction line instead.
          reason_code_code_description: split.code_description || "",
          reason_code_amount: roundMoneyAmount(split.amount),
          reason_code_execution_date: split.execution_date || "",
          reason_code_account_number:
            split.account_number || (split.expense_account ? extractAccountNumber(split.expense_account) : ""),
          promo_type: split.promo_type || "",
          promo_sub_type: split.promo_sub_type || "",
          product_category: split.product_category || "",
        })
        for (const field of SPLIT_OVERRIDE_FIELDS) {
          if (split[field]) {
            filtered2[field] = split[field]
          }
        }
        // Re-add org field values for this split row
        for (const orgField of activeOrgFields) {
          filtered2[orgField.field_name] = getOrgFieldValue(row, orgField.field_name, split)
        }
        filtered2.portal_link = addPortalLink(row)
        addSuperCoffeeSpecificFields(filtered2, row, null, split)
        // The accounting page returns coded lines as `splits` (not `reason_codes`), so the Guayaki
        // Items:* columns must be derived from each split's code_description here too.
        addGuayakiSpecificFields(filtered2, row, split)
        addXeroSpecificFields(filtered2, row, split)
        addWhoGivesACrapSpecificFields(filtered2, row, split)
        applyGoodPopDateFormatting(filtered2)
        filteredData.push(filtered2)
      }
      // Whatever the splits left unbooked gets its own "Uncoded" line, so summing "Net amount"
      // reaches the transaction's total instead of stopping at what someone got round to coding.
      const remainder = uncodedRemainderAmount(row, row.splits)
      if (remainder !== "") {
        let remainder_row = Object.assign({}, filtered, {
          expense_account: UNCODED_LABEL,
          split_amount: remainder,
          reason_code_name: UNCODED_LABEL,
          account_number: "",
        })
        for (const orgField of activeOrgFields) {
          remainder_row[orgField.field_name] = getOrgFieldValue(row, orgField.field_name)
        }
        remainder_row.portal_link = addPortalLink(row)
        addWhoGivesACrapSpecificFields(remainder_row, row)
        applyGoodPopDateFormatting(remainder_row)
        filteredData.push(remainder_row)
      }
    } else {
      // Nothing is coded on this transaction. It still gets a row, but "Net amount" used to come out
      // blank, so a month-end file that sums that column silently lost the money instead of showing
      // it as work outstanding: Saffron Road's July 2026 clearing list was $7,003.83 light because 41
      // invoices carried an early-pay discount nobody had coded yet. Emit the amount that has to be
      // booked, labelled "Uncoded" the same way a split with no reason code already is.
      const uncoded = uncodedExportAmount(row)
      if (uncoded !== "") {
        filtered.split_amount = uncoded
        filtered.expense_account = filtered.expense_account || UNCODED_LABEL
        filtered.reason_code_name = filtered.reason_code_name || UNCODED_LABEL
      }
      addWhoGivesACrapSpecificFields(filtered, row)
      applyGoodPopDateFormatting(filtered)
      filteredData.push(filtered)
    }
  }
  return filteredData
}

export function convertBackupToCsv(data: Record<string, number | string | boolean>[]) {
  if (!data || !data.length) {
    return ""
  }

  // Get all headers
  const allHeaders = Object.keys(data[0])

  // Filter out headers where all values are null, undefined, or empty string
  const headers = allHeaders.filter(header => {
    return data.some(item => {
      const value = item[header]
      return value !== null && value !== undefined && value !== ""
    })
  })

  const headerRow = headers.join(",")

  const dataRows = data
    .map(item => {
      return headers
        .map(header => {
          const cellValue = item[header]
          return `"${cellValue != null ? cellValue : ""}"`
        })
        .join(",")
    })
    .join("\n")

  return [headerRow, dataRows].join("\n")
}

/**
 * H-E-B's AP-AR Application, where the detail behind every H-E-B bill lives. It is behind
 * two-factor sign-in and has no per-document address, so this is the one place we can link.
 */
export const HEB_PORTAL_URL = "https://avp.heb.com/"

const TVI_PREFIXES = ["vcna", "vsup", "vcpn", "vonl", "viap"]

export function getTargetUrl(ded: Deduction): string {
  if (TVI_PREFIXES.some(prefix => ded.invoice_number?.toLowerCase().startsWith(prefix))) {
    let modified = ded.invoice_number.split(/[-:]/)[0].replace(/[^0-9]/g, "")
    return `https://tvi.partnersonline.com/quick-search/${modified}`
  } else if (ded.invoice_number?.toLowerCase().startsWith("cb")) {
    return "https://apreports.partnersonline.com/credit_debit"
  } else {
    return "https://www.partnersonline.com/page/help/NDI0"
  }
}

export function copyOnAltClick(text: string) {
  return function onClick(e: MouseEvent) {
    if (e.altKey) {
      e.preventDefault()
      e.stopImmediatePropagation()
      copyToClipboard(text)
      toast({ title: "Copied to clipboard" })
    }
  }
}

if (import.meta.vitest) {
  describe("uncodedExportAmount", () => {
    it("books the early-pay discount for an uncoded invoice", () => {
      // Kroger INV075092 on check 9023965 — present in Saffron's July export with a blank Net amount.
      expect(uncodedExportAmount({ kind: "invoice", invoice_amount: 14803.63, discount_amount: 302.09 })).toBe(302.09)
    })

    it("books an uncoded deduction positive and an uncoded repayment negative", () => {
      expect(uncodedExportAmount({ kind: "deduction", invoice_amount: -1517.81 })).toBe(1517.81)
      expect(uncodedExportAmount({ kind: "repayment", invoice_amount: 119.55 })).toBe(-119.55)
    })

    it("leaves prepayments and zero-discount invoices blank", () => {
      // A `:P`/`:N` prepayment pair nets to zero and is never coded — pricing it would double-count.
      expect(uncodedExportAmount({ kind: "prepayment", invoice_amount: -15429.12 })).toBe("")
      expect(uncodedExportAmount({ kind: "invoice", invoice_amount: 9844.43, discount_amount: 0 })).toBe("")
      expect(uncodedExportAmount({ kind: "invoice", invoice_amount: 9844.43, discount_amount: null })).toBe("")
    })
  })

  describe("uncodedRemainderAmount", () => {
    it("books what the splits left over on a half-coded deduction", () => {
      // UNFI West KGFSR072629346Y on Saffron's check 1725134: $1,104.99 owed, $144.13 coded.
      expect(uncodedRemainderAmount({ kind: "deduction", invoice_amount: -1104.99 }, [{ amount: 144.13 }])).toBe(960.86)
    })

    it("is blank when the splits close the transaction", () => {
      expect(
        uncodedRemainderAmount({ kind: "deduction", invoice_amount: -1104.99 }, [{ amount: 1000 }, { amount: 104.99 }])
      ).toBe("")
    })

    it("goes negative when a transaction is coded past its own amount", () => {
      // C&S INV074749 on check 2063648: a $178.43 early-pay discount carrying $1,142.75 of coding.
      expect(
        uncodedRemainderAmount({ kind: "invoice", invoice_amount: 8743.07, discount_amount: 178.43 }, [
          { amount: 1142.75 },
        ])
      ).toBe(-964.32)
    })

    it("keeps a repayment's remainder negative", () => {
      expect(uncodedRemainderAmount({ kind: "repayment", invoice_amount: 25378.56 }, [{ amount: -700 }])).toBe(-24678.56)
    })

    it("reads split amounts that arrive as strings, and skips prepayments", () => {
      expect(uncodedRemainderAmount({ kind: "deduction", invoice_amount: -1104.99 }, [{ amount: "144.13" }])).toBe(
        960.86
      )
      expect(uncodedRemainderAmount({ kind: "prepayment", invoice_amount: -15429.12 }, [{ amount: 1 }])).toBe("")
    })
  })

  describe("collectDownloadData", () => {
    it("adds an Uncoded line for what a partially coded transaction left over", () => {
      const rows = collectDownloadData([
        { id: "a", kind: "deduction", invoice_amount: -1104.99, splits: [{ amount: 144.13, name: "Fairshare" }] },
      ])
      expect(rows).toHaveLength(2)
      expect(rows[0].split_amount).toBe(144.13)
      expect(rows[1].split_amount).toBe(960.86)
      expect(rows[1].expense_account).toBe("Uncoded")
      // Summing the column now reaches the transaction's own amount.
      expect(rows.reduce((sum: number, r: any) => sum + Number(r.split_amount), 0)).toBeCloseTo(1104.99, 2)
    })

    it("adds no extra line when the splits already close the transaction", () => {
      const rows = collectDownloadData([
        {
          id: "a",
          kind: "deduction",
          invoice_amount: -300.73,
          splits: [{ amount: 162, name: "COS MCB: Snacks" }, { amount: 138.73, name: "Admin Fee" }],
        },
      ])
      expect(rows).toHaveLength(2)
      expect(rows.every((r: any) => r.expense_account !== "Uncoded")).toBe(true)
    })

    it("gives an uncoded transaction a Net amount instead of an empty cell", () => {
      const rows = collectDownloadData([
        { id: "a", kind: "invoice", invoice_amount: 14803.63, discount_amount: 302.09, splits: [] },
      ])
      expect(rows).toHaveLength(1)
      expect(rows[0].split_amount).toBe(302.09)
      expect(rows[0].expense_account).toBe("Uncoded")
      expect(rows[0].reason_code_name).toBe("Uncoded")
    })

    it("still leaves a prepayment's Net amount empty", () => {
      const rows = collectDownloadData([
        { id: "b", kind: "prepayment", invoice_amount: -15429.12, discount_amount: null, splits: [] },
      ])
      expect(rows[0].split_amount).toBeUndefined()
    })

    it("does not touch a transaction that already has splits", () => {
      const rows = collectDownloadData([
        {
          id: "c",
          kind: "deduction",
          invoice_amount: -229.28,
          splits: [
            { amount: 194.28, expense_account: "COS MCB: Snacks" },
            { amount: 35, expense_account: "Distributor Admin Fees Snack" },
          ],
        },
      ])
      expect(rows.map(r => r.split_amount)).toEqual([194.28, 35])
    })
  })

  describe("getTargetUrl", () => {
    it.each([
      ["VCNA0003420106-06", "https://tvi.partnersonline.com/quick-search/0003420106"],
      ["VSUP0002549886-01", "https://tvi.partnersonline.com/quick-search/0002549886"],
      ["VCPN0002537204-01", "https://tvi.partnersonline.com/quick-search/0002537204"],
      ["VONL00253720-101", "https://tvi.partnersonline.com/quick-search/00253720"],
      ["VIAP3490811", "https://tvi.partnersonline.com/quick-search/3490811"],
      ["VIAP3471694:2024-06-01", "https://tvi.partnersonline.com/quick-search/3471694"],
    ])("should return correct TVI URL for %s", (invoiceNumber, expectedUrl) => {
      const row = { invoice_number: invoiceNumber } as any
      expect(getTargetUrl(row)).toBe(expectedUrl)
    })

    it("should return credit/debit URL for CB prefix", () => {
      const row = { invoice_number: "CB-123" } as any
      expect(getTargetUrl(row)).toBe("https://apreports.partnersonline.com/credit_debit")
    })

    it("should return help URL for other cases", () => {
      const row = { invoice_number: "OTHER-123" } as any
      expect(getTargetUrl(row)).toBe("https://www.partnersonline.com/page/help/NDI0")
    })

    it("should handle lowercase invoice numbers", () => {
      const row = { invoice_number: "vcna123" } as any
      expect(getTargetUrl(row)).toBe("https://tvi.partnersonline.com/quick-search/123")
    })

    it("should handle undefined invoice number", () => {
      const row = {} as any
      expect(getTargetUrl(row)).toBe("https://www.partnersonline.com/page/help/NDI0")
    })
  })

  describe("convertBackupToCsv", () => {
    it("should convert an array of objects to CSV string", () => {
      const data = [
        { name: "John", age: 30, city: "New York" },
        { name: "Jane", age: 25, city: "Los Angeles" },
      ]
      const expected = 'name,age,city\n"John","30","New York"\n"Jane","25","Los Angeles"'
      expect(convertBackupToCsv(data)).toBe(expected)
    })

    it("should handle empty array", () => {
      expect(convertBackupToCsv([])).toBe("")
    })

    it("should handle objects with boolean values", () => {
      const data = [
        { name: "John", isStudent: true },
        { name: "Jane", isStudent: false },
      ]
      const expected = 'name,isStudent\n"John","true"\n"Jane","false"'
      expect(convertBackupToCsv(data)).toBe(expected)
    })

    it("should handle objects with special characters in values", () => {
      const data = [
        { name: 'John "Doe"', description: "Likes to use, commas" },
        { name: "Jane", description: "No special characters" },
      ]
      const expected = 'name,description\n"John "Doe"","Likes to use, commas"\n"Jane","No special characters"'
      expect(convertBackupToCsv(data)).toBe(expected)
    })

    it("should remove columns that are entirely empty, null, or undefined", () => {
      const data = [
        { name: "John", emptyStr: "", nullVal: null, undefinedVal: undefined, age: 30 },
        { name: "Jane", emptyStr: "", nullVal: null, undefinedVal: undefined, age: 25 },
      ]
      const expected = 'name,age\n"John","30"\n"Jane","25"'
      // @ts-ignore
      expect(convertBackupToCsv(data)).toBe(expected)
    })

    it("should keep columns that have at least one non-empty value", () => {
      const data = [
        { name: "John", col1: "", col2: null, col3: "value" },
        { name: "Jane", col1: "", col2: "data", col3: "" },
      ]
      const expected = 'name,col2,col3\n"John","","value"\n"Jane","data",""'
      // @ts-ignore
      expect(convertBackupToCsv(data)).toBe(expected)
    })

    it("should handle mixed data types in columns", () => {
      const data = [
        { name: "John", value: 123, empty: "" },
        { name: "Jane", value: "string", empty: null },
      ]
      const expected = 'name,value\n"John","123"\n"Jane","string"'
      // @ts-ignore
      expect(convertBackupToCsv(data)).toBe(expected)
    })
  })

  describe("collectDownloadData", () => {
    beforeEach(() => {
      // Mock window.location
      Object.defineProperty(window, "location", {
        value: {
          pathname: "/deductions",
          origin: "https://app.example.com",
          hostname: "app.example.com",
        },
        writable: true,
      })
    })

    it("uses the public app URL when the browser is on localhost", () => {
      Object.defineProperty(window, "location", {
        value: {
          pathname: "/deductions",
          origin: "http://localhost:5173",
          hostname: "localhost",
        },
        writable: true,
      })
      const result = collectDownloadData([{ deduction_id: "123", name: "John" }] as any)
      expect(result[0].portal_link).toBe("https://www.marginwiz.com/deduction/123")
    })

    it("should use deduction_id when available", () => {
      const input = [
        { id: "999", deduction_id: "123", name: "John" },
        { id: "888", deduction_id: "456", name: "Jane" },
      ]
      const result = collectDownloadData(input)

      expect(result[0].portal_link).toBe("https://app.example.com/deduction/123")
      expect(result[1].portal_link).toBe("https://app.example.com/deduction/456")
    })

    it("should fall back to id when deduction_id is not available", () => {
      const input = [
        { id: "123", name: "John" },
        { id: "456", name: "Jane" },
      ]
      const result = collectDownloadData(input)

      expect(result[0].portal_link).toBe("https://app.example.com/deduction/123")
      expect(result[1].portal_link).toBe("https://app.example.com/deduction/456")
    })

    it("should add split portal links when on accounting page", () => {
      // Update mock location to accounting page
      Object.defineProperty(window, "location", {
        value: {
          pathname: "/accounting",
          origin: "https://app.example.com",
          hostname: "app.example.com",
        },
        writable: true,
      })

      const input = [
        { id: "999", deduction_id: "123", name: "John" },
        { id: "888", deduction_id: "456", name: "Jane" },
      ]
      const result = collectDownloadData(input)

      expect(result[0].portal_link).toBe("https://app.example.com/accounting/transaction/123")
      expect(result[1].portal_link).toBe("https://app.example.com/accounting/transaction/456")
    })

    it("should add portal links to split rows", () => {
      const input = [
        {
          id: "999",
          deduction_id: "123",
          name: "John",
          splits: [
            { expense_account: "RC1", amount: 100 },
            { expense_account: "RC2", amount: 200 },
          ],
        },
      ]
      const result = collectDownloadData(input)

      expect(result.length).toBe(2)
      expect(result[0].portal_link).toBe("https://app.example.com/deduction/123")
      expect(result[1].portal_link).toBe("https://app.example.com/deduction/123")
      expect(result[0].reason_code_name).toBe("RC1")
      expect(result[1].reason_code_name).toBe("RC2")
    })

    it("should preserve existing data while adding portal links", () => {
      const input = [
        {
          id: "999",
          deduction_id: "123",
          name: "John",
          status: "active",
          amount: 100,
        },
      ]
      const result = collectDownloadData(input)

      expect(result[0]).toEqual({
        name: "John",
        status: "active",
        amount: 100,
        portal_link: "https://app.example.com/deduction/123",
        cleared: "no",
      })
    })

    it("should handle missing ids", () => {
      const input = [{ name: "John" }]
      const result = collectDownloadData(input)

      expect(result[0].portal_link).toBe("https://app.example.com/deduction/undefined")
    })

    it("should handle null deduction_id", () => {
      const input = [{ id: "123", deduction_id: null, name: "John" }]
      const result = collectDownloadData(input)

      expect(result[0].portal_link).toBe("https://app.example.com/deduction/123")
    })

    it("includes columns missing from the first row (union of keys across all rows)", () => {
      // Server omits Option fields (e.g. validated_date via skip_serializing_if) from the JSON
      // entirely, so the first row may lack keys that later rows have.
      const input = [
        { id: "123", name: "John" },
        { id: "456", name: "Jane", validated_date: "2026-06-12" },
      ]
      const result = collectDownloadData(input)

      expect(result[0]).toHaveProperty("validated_date")
      expect(result[0].validated_date).toBeUndefined()
      expect(result[1].validated_date).toBe("2026-06-12")
      // First-row key order is preserved; stragglers are appended after.
      expect(Object.keys(result[0]).indexOf("validated_date")).toBeGreaterThan(Object.keys(result[0]).indexOf("name"))
    })

    it("should include remote_accounting_id in the exported data", () => {
      const input = [
        {
          id: "123",
          name: "John",
          remote_accounting_id: "NS12345",
          some_other_id: "OTHER_ID", // This should be filtered out
        },
      ]
      const result = collectDownloadData(input)

      expect(result[0].remote_accounting_id).toBe("NS12345")
      expect(result[0].some_other_id).toBeUndefined()
    })

    it("should use split-specific promo ID for split export rows", () => {
      const input = [
        {
          id: "999",
          deduction_id: "123",
          promotion_remote_ids: "GP-001, GP-002",
          splits: [
            { expense_account: "RC1", amount: 100, promotion_remote_id: "GP-001" },
            { expense_account: "RC2", amount: 200, promotion_remote_id: "GP-002" },
          ],
        },
      ]
      const orgFieldConfigs = [
        {
          field_name: "promotion_remote_ids",
          display_name: "Promo IDs",
          is_active: true,
          table_contexts: ["accounting_table"],
        },
      ] as any

      const result = collectDownloadData(input as any, orgFieldConfigs)

      expect(result.length).toBe(2)
      expect(result[0].promotion_remote_ids).toBe("GP-001")
      expect(result[1].promotion_remote_ids).toBe("GP-002")
    })

    it("should keep transaction promo IDs for non-split rows", () => {
      const input = [
        {
          id: "123",
          deduction_id: "123",
          promotion_remote_ids: "GP-001, GP-002",
          name: "John",
        },
      ]
      const orgFieldConfigs = [
        {
          field_name: "promotion_remote_ids",
          display_name: "Promo IDs",
          is_active: true,
          table_contexts: ["accounting_table"],
        },
      ] as any

      const result = collectDownloadData(input as any, orgFieldConfigs)

      expect(result.length).toBe(1)
      expect(result[0].promotion_remote_ids).toBe("GP-001, GP-002")
    })

    it("should include check_detail_table org fields in exported data", () => {
      const input = [
        {
          id: "999",
          deduction_id: "123",
          splits: [{ expense_account: "RC1", amount: 100, custom_check_detail_field: "detail-value" }],
        },
      ]
      const orgFieldConfigs = [
        {
          field_name: "custom_check_detail_field",
          display_name: "Custom Check Detail Field",
          is_active: true,
          table_contexts: ["check_detail_table"],
        },
      ] as any

      const result = collectDownloadData(input as any, orgFieldConfigs)

      expect(result.length).toBe(1)
      expect(result[0].custom_check_detail_field).toBe("detail-value")
    })

    it("exports execution start and end dates from each GL split", () => {
      const input = [
        {
          id: "deduction-with-execution-window",
          kind: "deduction",
          invoice_amount: -100,
          pushed_at: "2026-05-31T18:00:00Z",
          splits: [
            {
              expense_account: "Deduction- Billback",
              amount: 100,
              promotion_start_date: "2026-05-03",
              promotion_end_date: "2026-05-09",
            },
          ],
        },
      ]
      const orgFieldConfigs = [
        {
          field_name: "pushed_at",
          display_name: "Pushed Date",
          is_active: true,
          table_contexts: ["accounting_table"],
        },
        {
          field_name: "promotion_start_date",
          display_name: "Execution Start Date",
          is_active: true,
          table_contexts: ["accounting_table"],
        },
        {
          field_name: "promotion_end_date",
          display_name: "Execution End Date",
          is_active: true,
          table_contexts: ["accounting_table"],
        },
      ] as any

      const [result] = collectDownloadData(input as any, orgFieldConfigs)

      expect(result.pushed_at).toBe("2026-05-31T18:00:00Z")
      expect(result.promotion_start_date).toBe("2026-05-03")
      expect(result.promotion_end_date).toBe("2026-05-09")
    })

    it("should only include active org fields from CSV export contexts", () => {
      const input = [
        {
          id: "999",
          deduction_id: "123",
          splits: [
            {
              expense_account: "RC1",
              amount: 100,
              accounting_field: "acct-value",
              check_detail_field: "check-detail-value",
              hidden_field: "hidden-value",
            },
          ],
        },
      ]
      const orgFieldConfigs = [
        {
          field_name: "accounting_field",
          display_name: "Accounting Field",
          is_active: true,
          table_contexts: ["accounting_table"],
        },
        {
          field_name: "check_detail_field",
          display_name: "Check Detail Field",
          is_active: true,
          table_contexts: ["check_detail_table"],
        },
        {
          field_name: "hidden_field",
          display_name: "Hidden Field",
          is_active: true,
          table_contexts: ["other_table"],
        },
        {
          field_name: "inactive_field",
          display_name: "Inactive Field",
          is_active: false,
          table_contexts: ["check_detail_table"],
        },
      ] as any

      const result = collectDownloadData(input as any, orgFieldConfigs)

      expect(result[0].accounting_field).toBe("acct-value")
      expect(result[0].check_detail_field).toBe("check-detail-value")
      expect(result[0].hidden_field).toBeUndefined()
      expect(result[0].inactive_field).toBeUndefined()
    })

    it("should export a direct billing's settlement type and deduction method", () => {
      const result = collectDownloadData([
        {
          invoice_number: "180121-7",
          kind: "deduction",
          import_source: "admin_portal_direct_bill",
          custom_deduction_method: "Pay Customer Directly",
        },
        { invoice_number: "INV1", kind: "deduction", import_source: "portal", custom_deduction_method: null },
      ])
      expect(result[0].kind).toBe("direct billing")
      expect(result[0].custom_deduction_method).toBe("Check request")
      expect(result[1].kind).toBe("deduction")
      expect(result[1].custom_deduction_method).toBe("")
    })

    it("should export clearing_list from the posting month when active", () => {
      const input = [
        {
          id: "999",
          deduction_id: "123",
          posting_date: "2026-06-01",
          status_value: "validated",
          validated_date: "2026-07-20",
          splits: [{ expense_account: "RC1", amount: 100 }],
        },
      ]
      const orgFieldConfigs = [
        {
          field_name: "clearing_list",
          display_name: "Clearing List",
          is_active: true,
          table_contexts: ["accounting_table"],
        },
      ] as any

      const result = collectDownloadData(input as any, orgFieldConfigs)

      expect(result.length).toBe(1)
      expect(result[0].clearing_list).toBe("June 2026")
    })

    it("should export split rows with clearing_list from the parent row", () => {
      const input = [
        {
          id: "999",
          deduction_id: "123",
          posting_date: "2026-06-30",
          status_value: "validated",
          validated_date: "2026-06-30",
          splits: [
            { expense_account: "RC1", amount: 100 },
            { expense_account: "RC2", amount: 200 },
          ],
        },
      ]
      const orgFieldConfigs = [
        {
          field_name: "clearing_list",
          display_name: "Clearing List",
          is_active: true,
          table_contexts: ["accounting_table"],
        },
      ] as any

      const result = collectDownloadData(input as any, orgFieldConfigs)

      expect(result.length).toBe(2)
      expect(result[0].clearing_list).toBe("June 2026")
      expect(result[1].clearing_list).toBe("June 2026")
    })
  })

  describe("GoodPop YYYY-MM formatting", () => {
    beforeEach(() => {
      vi.spyOn(UserState, "get").mockImplementation(() => ({
        org_id: GOODPOP_ORG_IDS[0],
        email: "test@example.com",
        id: "123",
        first_name: "Test",
        last_name: "User",
        role: "admin",
      }))
    })

    afterEach(() => {
      vi.restoreAllMocks()
    })

    it("formats base execution_date to YYYY-MM for GoodPop", () => {
      const input = [{ id: "1", execution_date: "2025-01-07", name: "Row" }] as any
      const result = collectDownloadData(input)
      expect(result[0].execution_date).toBe("2025-01")
    })

    it("formats reason_code_execution_date to YYYY-MM for GoodPop", () => {
      const input = [
        {
          id: "1",
          reason_codes: [{ code_description: "x", amount: 1, execution_date: "2025-02-15" }],
        },
      ] as any
      const result = collectDownloadData(input)
      expect(result[0].reason_code_execution_date).toBe("2025-02")
    })

    it("does not change dates for non-GoodPop org", () => {
      vi.spyOn(UserState, "get").mockImplementation(
        () =>
          ({
            org_id: "other-org",
          }) as any
      )
      const input = [{ id: "1", execution_date: "2025-01-07" }] as any
      const result = collectDownloadData(input)
      expect(result[0].execution_date).toBe("2025-01-07")
    })
  })

  describe("build_csv function", () => {
    const mockDeductionResponse: DeductionListJson[] = [
      {
        invoice_amount: 100,
        source: "mock-source",
        invoice_number: "mock-invoice-number",
        check_number: "123",
        po_number: "mock-po-number",
        retailer_name: "mock-retailer-name",
        category: "mock-category",
        description: "mock-description",
        status_value: "mock-status-value",
        splits: [
          {
            expense_account: "mock-expense-account",
          },
        ],
      } as any,
    ]

    it("should return a CSV string with mapped headers when org_id exists in HEADER_MAP", () => {
      const org_id = TEST_ORG_ID
      const result = buildCsv(collectDownloadData(mockDeductionResponse), org_id)

      const lines = result.split("\n")
      const headers = lines[0]
      const values = lines[1]

      // Check that invoice_amount is mapped to UnitPrice
      expect(headers).toContain('"UnitPrice"')

      // Check that unpacked reason_code fields are present with their mapped names
      expect(headers).toContain('"ExpenseAccount"')

      // Check values
      expect(values).toContain('"100"') // invoice_amount/UnitPrice
      expect(values).toContain('"mock-expense-account"')
    })

    it("should use display_name headers for check_detail_table org fields", () => {
      const org_id = "some-org"
      const filteredData = [{ check_detail_field: "detail-value" }]
      const orgFieldConfigs = [
        {
          field_name: "check_detail_field",
          display_name: "Check Detail Field",
          is_active: true,
          table_contexts: ["check_detail_table"],
        },
      ] as any

      const result = buildCsv(filteredData, org_id, orgFieldConfigs)
      const headers = result.split("\n")[0]

      expect(headers).toContain('"Check Detail Field"')
    })
  })

  describe("PromoMash export (Saffron Road)", () => {
    // Mirrors KeHE deduction 0477043 from the accounting export, splits and all: they sum to the
    // transaction's own 294.72, which is what stops the export adding an "Uncoded" remainder line.
    const promomashResponse: DeductionListJson[] = [
      {
        id: "1",
        kind: "deduction",
        source: "kehe",
        invoice_number: "0477043",
        check_number: "3280204",
        invoice_amount: -294.72,
        admin_fee: 65,
        discount_amount: -6.01,
        status_value: "new",
        validated: false,
        validated_date: null,
        splits: [
          { name: "COS MCB: Snacks", account_number: "02-4606-01", amount: 162 },
          { name: "Distributor Administration Fee Culinary", account_number: "01-5422-01", amount: 65 },
          { name: "COS MCB: Culinary", account_number: "01-4606-01", amount: 47.88 },
          { name: "COS MCB: Entrees", account_number: "01-4606-02", amount: 19.84 },
        ],
      } as any,
    ]

    function promomashHeaderRow(data: DeductionListJson[]): string {
      const rows = filterDownloadDataBySelectedColumns(collectDownloadData(data), PROMOMASH_CSV_COLUMNS)
      return buildCsv(rows, SAFFRON_ROAD_ORG_ID).split("\n")[0]
    }

    it("renames headers to the PromoMash labels in PromoMash order", () => {
      const headers = promomashHeaderRow(promomashResponse)
      // clearing_list / is_trade are org fields (absent without OrgFieldConfig), so they drop out.
      expect(headers).toBe(
        [
          "Check number",
          "Deduction invoice",
          "Settlement type",
          "Direct PM customer",
          "Amount",
          "Fees",
          "Adjustment",
          "Net amount",
          "Fund",
          "GL account",
          "Validation status",
          "Cleared",
          "Closed date",
        ]
          .map(h => `"${h}"`)
          .join(",")
      )
    })

    it("omits PromoMash columns we have no data for", () => {
      const headers = promomashHeaderRow(promomashResponse)
      for (const missing of ["PM reason code", "PM reason code category", "Direct PM customer division", "Direct PM customer ID"]) {
        expect(headers).not.toContain(`"${missing}"`)
      }
    })

    it("derives the Cleared column from validated (yes/no)", () => {
      const notCleared = collectDownloadData(promomashResponse)
      expect(notCleared.every((row: any) => row.cleared === "no")).toBe(true)

      const validated = [{ ...promomashResponse[0], validated: true }] as any
      const cleared = collectDownloadData(validated)
      expect(cleared.every((row: any) => row.cleared === "yes")).toBe(true)
    })

    it("emits one row per split with the split amount as Net amount", () => {
      const rows = filterDownloadDataBySelectedColumns(collectDownloadData(promomashResponse), PROMOMASH_CSV_COLUMNS)
      expect(rows).toHaveLength(4)
      expect(rows.map((r: any) => r.split_amount)).toEqual([162, 65, 47.88, 19.84])
      expect(rows.map((r: any) => r.reason_code_name)).toEqual([
        "COS MCB: Snacks",
        "Distributor Administration Fee Culinary",
        "COS MCB: Culinary",
        "COS MCB: Entrees",
      ])
    })
  })

  describe("CSV column selection helpers", () => {
    it("maps every Guayaki NetSuite default to the reviewed workbook header in order", () => {
      const row = Object.fromEntries(GUAYAKI_NETSUITE_CSV_COLUMNS.map(column => [column, "value"]))
      const header = buildCsv([row], GUAYAKI_ORG_ID).split("\n")[0]

      expect(header).toBe(
        [
          "Items: Line Number",
          "kind",
          "invoice_amount",
          "discount_amount",
          "gross_amount",
          "invoice_date",
          "invoice_number",
          "check_number",
          "PO #",
          "dc",
          "retailer_name",
          "Items: MCB Date",
          "description",
          "category",
          "category_description",
          "status_value",
          "Customer",
          "Customer ID",
          "Date",
          "External ID",
          "Location",
          "Memo",
          "CM: deptID",
          "CM: classID",
          "CM: regionID",
          "CM: channelID",
          "Items: DeptID",
          "Items: classID",
          "Items: regionID",
          "Items: channelID",
          "Items: Price Level",
          "expense_account",
          "split_amount",
          "reason_code_name",
          "account_number",
          "Items: Item",
          "Items: ItemID",
          "Items: Description",
          "Items: Quantity",
          "Items: Proposed Rate",
          "Items: Rate",
        ]
          .map(column => `"${column}"`)
          .join(",")
      )
    })

    it("should derive exportable columns preserving first-row order and appending new keys", () => {
      const filteredData = [
        { invoice_number: "INV-1", amount: 100, customer_name: "A" },
        { invoice_number: "INV-2", amount: 200, account_number: "5100" },
      ]

      expect(getAvailableExportColumns(filteredData)).toEqual([
        "invoice_number",
        "amount",
        "customer_name",
        "account_number",
      ])
    })

    it("merges Guayaki RTM split columns into the default export selection", () => {
      const availableColumns = [
        "invoice_number",
        "split_amount",
        "items_deptId",
        "items_classId",
        "items_regionId",
        "execution_date",
        "customer_name",
      ]

      expect(mergeGuayakiRtmSplitExportColumns(["invoice_number", "split_amount"], availableColumns)).toEqual([
        "invoice_number",
        "split_amount",
        "execution_date",
        "items_deptId",
        "items_classId",
        "items_regionId",
      ])
    })

    it("should apply selected columns in selected order", () => {
      const filteredData = [
        { invoice_number: "INV-1", amount: 100, customer_name: "A" },
        { invoice_number: "INV-2", amount: 200, customer_name: "B" },
      ]

      expect(filterDownloadDataBySelectedColumns(filteredData, ["customer_name", "invoice_number"])).toEqual([
        { customer_name: "A", invoice_number: "INV-1" },
        { customer_name: "B", invoice_number: "INV-2" },
      ])
    })

    it("should fall back to original data when selected columns are empty or invalid", () => {
      const filteredData = [{ invoice_number: "INV-1", amount: 100 }]

      expect(filterDownloadDataBySelectedColumns(filteredData, [])).toEqual(filteredData)
      expect(filterDownloadDataBySelectedColumns(filteredData, ["missing_column"])).toEqual(filteredData)
    })
  })

  describe("parseCodeDescription", () => {
    it("should correctly parse code description fields", () => {
      const description =
        "Items: DeptID-462. Items: classID-2.Items: regionID-35.Items: channelID-2.Items: Price Level-Custom"
      const result = parseCodeDescription(description)

      expect(result).toEqual({
        deptId: "462",
        classId: "2",
        regionId: "35",
        channelId: "2",
        priceLevel: "Custom",
      })
    })

    it("should handle missing fields", () => {
      const description = "Items: DeptID-462. Items: Price Level-Custom"
      const result = parseCodeDescription(description)

      expect(result).toEqual({
        deptId: "462",
        priceLevel: "Custom",
      })
    })

    it("should handle empty or undefined input", () => {
      expect(parseCodeDescription()).toEqual({})
      expect(parseCodeDescription("")).toEqual({})
    })
  })

  describe("collectDownloadData with code description parsing", () => {
    it("should parse code description for Guayaki orgs", () => {
      const testData = [
        {
          org_id: GUAYAKI_ORG_ID,
          reason_codes: [
            {
              code_description:
                "Items: DeptID-462. Items: classID-2.Items: regionID-35.Items: channelID-2.Items: Price Level-Custom",
              amount: 100,
              execution_date: "2025-01-07",
            },
          ],
        },
      ]

      const result = collectDownloadData(testData)

      expect(result[0]).toMatchObject({
        portal_link: expect.any(String),
        reason_code_code_description:
          "Items: DeptID-462. Items: classID-2.Items: regionID-35.Items: channelID-2.Items: Price Level-Custom",
      })
    })

    it("should not parse code description for other orgs", () => {
      const testData = [
        {
          org_id: "other_org",
          reason_codes: [
            {
              code_description: "Items: DeptID-462. Items: classID-2",
            },
          ],
        },
      ]

      const result = collectDownloadData(testData)

      expect(result[0]).not.toHaveProperty("items_deptId")
      expect(result[0]).not.toHaveProperty("items_classId")
    })
  })

  // Regression: the accounting page (/v2/accounting/list) returns coded lines as `splits`, not
  // `reason_codes` — the Items:* columns must be populated from that shape too, or the Guayaki
  // NetSuite export goes out with blank Items columns.
  describe("collectDownloadData with accounting splits", () => {
    beforeEach(() => {
      vi.spyOn(UserState, "get").mockImplementation(() => ({
        org_id: GUAYAKI_ORG_ID,
        email: "test@example.com",
        id: "123",
        first_name: "Test",
        last_name: "User",
        role: "admin",
      }))
    })

    afterEach(() => {
      vi.restoreAllMocks()
    })

    it("populates Items:* columns and split_amount from each split's code_description", () => {
      const testData = [
        {
          org_id: GUAYAKI_ORG_ID,
          check_number: "3236464",
          invoice_date: "2025-12-22",
          category_description: "Sprouts Return By Customer",
          dc: "27",
          retailer_name: "Sprouts",
          splits: [
            {
              code_description:
                "Items: DeptID-463. Items: classID-360. Items: regionID-27. Items: channelID-2. Items: Price Level-Custom",
              amount: -141.52,
              name: "490005 Sales Adjustment",
              expense_account: "490005 Sales Adjustment : Allowances : Returns & Allowances",
              account_remote_id: "531",
              execution_date: "2025-12-22",
            },
            {
              code_description:
                "Items: DeptID-462. Items: classID-362. Items: regionID-35. Items: channelID-2. Items: Price Level-Custom",
              amount: -947.12,
              name: "490005 Sales Adjustment",
              expense_account: "490005 Sales Adjustment : Allowances : Returns & Allowances",
              account_remote_id: "531",
              execution_date: "2025-12-22",
            },
          ],
        },
      ]

      const result = collectDownloadData(testData)

      expect(result).toHaveLength(2)
      expect(result[0]).toMatchObject({
        items_line_number: 1,
        split_amount: -141.52,
        items_deptId: "463",
        items_classId: "360",
        items_regionId: "27",
        items_channelId: "2",
        items_priceLevel: "Custom",
        items_item: "490005 Sales Adjustment : Allowances : Returns & Allowances",
        items_itemId: "531",
        items_description: "Sprouts Return By Customer - 27: Sprouts: Dec 2025",
        items_proposed_rate: "141.52",
        execution_date: "Dec 2025",
        cm_deptId: "462",
        cm_classId: "362",
        cm_regionId: "17",
        cm_channelId: "2",
      })
      expect(result[1]).toMatchObject({
        items_line_number: 1,
        split_amount: -947.12,
        items_deptId: "462",
        items_classId: "362",
        items_regionId: "35",
        items_proposed_rate: "947.12",
      })
      expect(result.map(row => Number(row.items_proposed_rate))).toEqual(result.map(row => Math.abs(row.split_amount)))
    })

    it("rounds split_amount to two decimal places in export", () => {
      const testData = [
        {
          org_id: GUAYAKI_ORG_ID,
          splits: [
            {
              code_description:
                "Items: DeptID-462. Items: classID-362. Items: regionID-26. Items: channelID-2. Items: Price Level-Custom",
              amount: 0.3192,
              name: "490005 Sales Adjustment",
            },
            {
              code_description:
                "Items: DeptID-462. Items: classID-362. Items: regionID-29. Items: channelID-2. Items: Price Level-Custom",
              amount: 2.3408,
              name: "490005 Sales Adjustment",
            },
          ],
        },
      ]

      const result = collectDownloadData(testData)

      expect(result).toHaveLength(2)
      expect(result[0].split_amount).toBe(0.32)
      expect(result[1].split_amount).toBe(2.34)
    })

    it("defaults credit memo price level to Custom when coding metadata omits it", () => {
      const result = collectDownloadData([
        {
          org_id: GUAYAKI_ORG_ID,
          splits: [{ code_description: "Items: DeptID-462", amount: -5 }],
        },
      ])

      expect(result[0].items_priceLevel).toBe("Custom")
    })

    it("increments line numbers per transaction while keeping split rows together", () => {
      const result = collectDownloadData([
        {
          org_id: GUAYAKI_ORG_ID,
          splits: [
            { code_description: "Items: DeptID-462", amount: -3 },
            { code_description: "Items: DeptID-463", amount: -7 },
          ],
        },
        {
          org_id: GUAYAKI_ORG_ID,
          splits: [{ code_description: "Items: DeptID-488", amount: -5 }],
        },
      ])

      expect(result.map(row => row.items_line_number)).toEqual([1, 1, 2])
    })

    it("does not add Items:* columns for non-Guayaki orgs", () => {
      vi.spyOn(UserState, "get").mockImplementation(() => ({
        org_id: "some-other-org",
        email: "test@example.com",
        id: "123",
        first_name: "Test",
        last_name: "User",
        role: "admin",
      }))

      const testData = [
        {
          org_id: "some-other-org",
          splits: [{ code_description: "Items: DeptID-462. Items: classID-362", amount: -5 }],
        },
      ]

      const result = collectDownloadData(testData)

      expect(result[0]).not.toHaveProperty("items_deptId")
      expect(result[0].split_amount).toBe(-5)
    })

    it("includes is_trade per split only when org field config is active", () => {
      const is_trade_config = {
        id: "cfg-1",
        org_id: "org-1",
        field_name: "is_trade",
        field_type: FieldType.Text,
        display_name: "Is Trade",
        display_order: 0,
        is_active: true,
        table_contexts: ["accounting_table"],
        created_at: "",
        updated_at: "",
      }
      const testData = [
        {
          org_id: "org-1",
          invoice_number: "INV-1",
          splits: [
            { name: "Trade fund", amount: -10, is_trade: true },
            { name: "Non-trade fund", amount: -5, is_trade: false },
            { name: "Uncoded", amount: -1 },
          ],
        },
      ]

      const without_config = collectDownloadData(testData)
      expect(without_config[0]).not.toHaveProperty("is_trade")

      const with_config = collectDownloadData(testData, [is_trade_config])
      expect(with_config).toHaveLength(3)
      expect(with_config[0].is_trade).toBe("true")
      expect(with_config[1].is_trade).toBe("false")
      expect(with_config[2].is_trade).toBe("")
    })
  })

  describe("collectDownloadData split-level detail", () => {
    beforeEach(() => {
      vi.spyOn(UserState, "get").mockImplementation(() => ({
        org_id: "01926d97-4d3d-9bbe-29b7-99cc573c2e7b",
        email: "test@example.com",
        id: "123",
        first_name: "Test",
        last_name: "User",
        role: "admin",
      }))
    })

    afterEach(() => {
      vi.restoreAllMocks()
    })

    // Nancy's KeHE check 3311940 exported five identical rows for 170585IA — one per split, but each
    // one repeating the transaction's blank product line and retailer instead of the split's.
    it("exports each split's own coding detail rather than repeating the transaction", () => {
      const result = collectDownloadData([
        {
          id: "01a03cde-9a5c-10f7-dcf4-7777daf0b9e3",
          kind: "deduction",
          invoice_number: "170585IA",
          invoice_amount: -1133.42,
          discount_amount: -23.13,
          description: "170585IA",
          product_line: null,
          retailer_name: null,
          retailer_invoice_number: null,
          execution_date: null,
          splits: [
            {
              amount: 258.22,
              name: "522400 Qty discrepancy",
              expense_account: "522400 Qty discrepancy",
              account_number: "522400",
              code_description: "Quantity Variance / Shortage - YOGURT NF PLAIN ORG",
              product_line: "Yogurt",
              retailer_name: "KeHE",
              retailer_invoice_number: "170585IA",
              product_category: "Dairy",
              promo_type: "SCAN",
              promo_sub_type: "In-store",
              execution_date: "2026-08-22",
            },
            {
              amount: 45.71,
              name: "522400 Qty discrepancy",
              expense_account: "522400 Qty discrepancy",
              account_number: "522400",
              code_description: "Spoilage Allowance (3.00%) - CHEESE COTTAGE WHL MLK OR",
              product_line: "Cottage Cheese",
              retailer_name: "KeHE",
              retailer_invoice_number: "170585IA",
            },
          ],
        },
      ])

      // Two splits plus the "Uncoded" remainder line for what they leave unbooked.
      expect(result).toHaveLength(3)
      expect(result[0]).toMatchObject({
        split_amount: 258.22,
        reason_code_code_description: "Quantity Variance / Shortage - YOGURT NF PLAIN ORG",
        reason_code_amount: 258.22,
        reason_code_execution_date: "2026-08-22",
        reason_code_account_number: "522400",
        product_line: "Yogurt",
        retailer_name: "KeHE",
        retailer_invoice_number: "170585IA",
        product_category: "Dairy",
        promo_type: "SCAN",
        promo_sub_type: "In-store",
        execution_date: "2026-08-22",
      })
      expect(result[1]).toMatchObject({
        split_amount: 45.71,
        reason_code_code_description: "Spoilage Allowance (3.00%) - CHEESE COTTAGE WHL MLK OR",
        product_line: "Cottage Cheese",
        product_category: "",
        promo_type: "",
      })
      // The split leaves the transaction's own columns alone where it carries no value of its own.
      expect(result[1].execution_date).toBeNull()
      expect(result[1].description).toBe("170585IA")
      // The uncoded remainder is not a split, so it keeps the transaction-level values.
      expect(result[2]).toMatchObject({ reason_code_name: "Uncoded", product_line: null })
    })
  })

  describe("addGuayakiSpecificFields", () => {
    beforeEach(() => {
      vi.spyOn(UserState, "get").mockImplementation(() => ({
        org_id: GUAYAKI_ORG_ID,
        email: "test@example.com",
        id: "123",
        first_name: "Test",
        last_name: "User",
        role: "admin",
      }))
    })

    afterEach(() => {
      vi.restoreAllMocks()
    })

    it("should add all required fields with exact column names", () => {
      const baseItem = {
        source: "kehe",
        invoice_amount: -54.23,
        invoice_date: "2024-12-17",
        check_number: "3113707",
        check_date: "2024-12-20",
        check_amount: 40675.89,
        po_number: "1234",
        dc: "55",
        location: "GSRP HQ",
        memo: "MarginWiz Deduction Platform - Accounting Export",
        reason_code_expense_account: "62990 - Misc",
      }

      const arrayItem = {
        code_description:
          "Items: DeptID-462. Items: classID-2.Items: regionID-29.Items: channelID-2.Items: Price Level-Custom",
        amount: 47.7224,
        expense_account: "62990 - Misc",
        execution_date: "2025-01-07",
        actor: "promoted",
        is_split: true,
        customer_name: "kehe",
        remote_id: "123",
      }

      addGuayakiSpecificFields(baseItem, baseItem, arrayItem)

      expect(baseItem).toMatchObject({
        customer: "KeHE",
        customer_id: "107475",
        date: expect.stringMatching(/^\d{2}\/\d{2}\/\d{4}$/),
        external_id: expect.stringMatching(/^107475-\d{5}3113707$/),
        location: "GSRP HQ",
        po_number: "1234",
        memo: "MarginWiz Deduction Platform - Accounting Export",
        items_itemId: "123",
        items_description: "62990 - Misc: Jan 2025",
        items_quantity: "1",
        items_proposed_rate: "47.72",
        items_rate: "0.00",
        execution_date: "Jan 2025",
        items_deptId: "462",
        items_classId: "2",
        items_regionId: "29",
        items_channelId: "2",
        items_priceLevel: "Custom",
        cm_deptId: "462",
        cm_classId: "362",
        cm_regionId: "17",
        cm_channelId: "2",
      })
    })

    it("should format MCB date correctly", () => {
      const baseItem = {
        reason_code_expense_account: "62990 - Misc",
      } as any
      const arrayItem = {
        execution_date: "2025-01-07",
        amount: 100,
      }

      addGuayakiSpecificFields(baseItem, baseItem, arrayItem)
      expect(baseItem.execution_date).toBe("Jan 2025")
    })

    it("should format description correctly", () => {
      const baseItem = {
        reason_code_expense_account: "62990 - Misc",
      } as any
      const arrayItem = {
        execution_date: "2025-01-07",
        amount: 100,
      }

      addGuayakiSpecificFields(baseItem, baseItem, arrayItem)
      expect(baseItem.items_description).toBe("62990 - Misc: Jan 2025")
    })

    it("should handle missing arrayItem gracefully", () => {
      const baseItem = {
        code_description:
          "Items: DeptID-462. Items: classID-2.Items: regionID-29.Items: channelID-2.Items: Price Level-Custom",
      } as any

      addGuayakiSpecificFields(baseItem, baseItem)

      expect(baseItem).toMatchObject({
        items_deptId: "462",
        items_classId: "2",
        items_regionId: "29",
        items_channelId: "2",
        items_priceLevel: "Custom",
        customer: "KeHE",
        customer_id: "107475",
        cm_deptId: "462",
        cm_classId: "362",
        cm_regionId: "17",
        cm_channelId: "2",
      })

      // Should not have array-item specific fields
      expect(baseItem).not.toHaveProperty("items_item")
      expect(baseItem).not.toHaveProperty("items_quantity")
      expect(baseItem).not.toHaveProperty("items_proposed_rate")
    })

    it("should generate unique external IDs with check number when available", () => {
      const baseItem1 = { check_number: "12345" } as any
      const baseItem2 = { check_number: "67890" } as any

      addGuayakiSpecificFields(baseItem1, baseItem1)
      addGuayakiSpecificFields(baseItem2, baseItem2)

      expect(baseItem1.external_id).toMatch(/^107475-\d{5}12345$/)
      expect(baseItem2.external_id).toMatch(/^107475-\d{5}67890$/)
      expect(baseItem1.external_id).not.toBe(baseItem2.external_id)
    })

    it("should generate external IDs with random letter when check number is missing", () => {
      const baseItem = {} as any

      addGuayakiSpecificFields(baseItem, baseItem)

      expect(baseItem.external_id).toMatch(/^107475-\d{5}[a-z]$/)
    })

    it("should not modify baseItem if user org is not Guayaki", () => {
      vi.spyOn(UserState, "get").mockImplementation(() => ({
        org_id: "other-org",
        email: "test@example.com",
        id: "123",
        first_name: "Test",
        last_name: "User",
        role: "admin",
      }))

      const baseItem = {
        original_field: "value",
      }

      addGuayakiSpecificFields(baseItem, baseItem)

      expect(baseItem).toEqual({
        original_field: "value",
      })
    })

    it("should handle null user gracefully", () => {
      vi.spyOn(UserState, "get").mockImplementation(() => null)

      const baseItem = {
        original_field: "value",
      }

      addGuayakiSpecificFields(baseItem, baseItem)

      expect(baseItem).toEqual({
        original_field: "value",
      })
    })
  })

  describe("addGuayakiSpecificFields MCB date formatting", () => {
    beforeEach(() => {
      vi.spyOn(UserState, "get").mockImplementation(() => ({
        org_id: GUAYAKI_ORG_ID,
        email: "test@example.com",
        id: "123",
        first_name: "Test",
        last_name: "User",
        role: "admin",
      }))
    })

    afterEach(() => {
      vi.restoreAllMocks()
    })

    it("should format MCB date as 'MMM YYYY'", () => {
      const baseItem = {
        reason_code_expense_account: "62990 - Misc",
      } as any
      const arrayItem = {
        execution_date: "2023-12-15",
        amount: 100,
      }

      addGuayakiSpecificFields(baseItem, baseItem, arrayItem)
      expect(baseItem.execution_date).toBe("Dec 2023")
      expect(baseItem.items_description).toBe("62990 - Misc: Dec 2023")
    })
  })

  describe("getExcelSerialDate", () => {
    it("should convert dates to Excel serial numbers", () => {
      expect(getExcelSerialDate(new Date(2024, 11, 31))).toBe(45657) // 12/31/2024
      expect(getExcelSerialDate(new Date(2024, 0, 1))).toBe(45292) // 1/1/2024
      expect(getExcelSerialDate(new Date(2023, 11, 31))).toBe(45291) // 12/31/2023
    })
  })

  describe("remapKeheContactName", () => {
    it("should remap 'kehe' to 'Kehe Distributors LLC'", () => {
      expect(remapKeheContactName("kehe")).toBe("Kehe Distributors LLC")
      expect(remapKeheContactName("Kehe")).toBe("Kehe Distributors LLC")
      expect(remapKeheContactName("KEHE")).toBe("Kehe Distributors LLC")
      expect(remapKeheContactName("  kehe  ")).toBe("Kehe Distributors LLC")
    })

    it("should remap semicolon-separated values containing 'kehe' to 'Kehe Distributors LLC'", () => {
      expect(remapKeheContactName("kehe;other")).toBe("Kehe Distributors LLC")
      expect(remapKeheContactName("other;kehe")).toBe("Kehe Distributors LLC")
      expect(remapKeheContactName("value1;kehe;value2")).toBe("Kehe Distributors LLC")
      expect(remapKeheContactName("Kehe;Other")).toBe("Kehe Distributors LLC")
      expect(remapKeheContactName("  kehe  ;  other  ")).toBe("Kehe Distributors LLC")
    })

    it("should not remap values that don't contain 'kehe'", () => {
      expect(remapKeheContactName("unfi")).toBe("unfi")
      expect(remapKeheContactName("other;value")).toBe("other;value")
      expect(remapKeheContactName("")).toBe("")
    })

    it("should handle empty and null values", () => {
      expect(remapKeheContactName("")).toBe("")
      // @ts-ignore - testing null handling
      expect(remapKeheContactName(null)).toBe(null)
    })
  })

  describe("addXeroSpecificFields with Kehe remapping", () => {
    beforeEach(() => {
      vi.spyOn(UserState, "get").mockImplementation(() => ({
        org_id: COMPOSTIC_ORG_ID,
        email: "test@example.com",
        id: "123",
        first_name: "Test",
        last_name: "User",
        role: "admin",
      }))
    })

    afterEach(() => {
      vi.restoreAllMocks()
    })

    it("should remap 'kehe' contact_name to 'Kehe Distributors LLC'", () => {
      const baseItem = {} as any
      const item = {
        customer_name: "kehe",
        invoice_number: "INV-123",
        invoice_date: "2024-01-01",
      }

      addXeroSpecificFields(baseItem, item)

      expect(baseItem.contact_name).toBe("Kehe Distributors LLC")
    })

    it("should remap semicolon-separated contact_name containing 'kehe' to 'Kehe Distributors LLC'", () => {
      const baseItem = {} as any
      const item = {
        customer_name: "kehe;other",
        invoice_number: "INV-123",
        invoice_date: "2024-01-01",
      }

      addXeroSpecificFields(baseItem, item)

      expect(baseItem.contact_name).toBe("Kehe Distributors LLC")
    })

    it("should not remap non-Kehe contact names", () => {
      const baseItem = {} as any
      const item = {
        customer_name: "unfi",
        invoice_number: "INV-123",
        invoice_date: "2024-01-01",
      }

      addXeroSpecificFields(baseItem, item)

      expect(baseItem.contact_name).toBe("unfi")
    })

    it("should not modify contact_name for non-Compostic orgs", () => {
      vi.spyOn(UserState, "get").mockImplementation(
        () =>
          ({
            org_id: "other-org",
          }) as any
      )

      const baseItem = { customer_name: "kehe" } as any
      const item = {
        customer_name: "kehe",
        invoice_number: "INV-123",
        invoice_date: "2024-01-01",
      }

      addXeroSpecificFields(baseItem, item)

      // Function returns early for non-Compostic orgs, so contact_name should not be set
      expect(baseItem.contact_name).toBeUndefined()
      // customer_name should remain unchanged
      expect(baseItem.customer_name).toBe("kehe")
    })
  })
}
